import { useEffect, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';

import { Shell } from './components/Shell';
import { BILLING_PATH, Mark } from './components/ui';
import { pageview } from './lib/analytics';
import { BillingProvider, SessionProvider, useSession } from './lib/session';
import { ForgotPassword, ResetPassword, safeNext, SignIn, SignUp, SsoCallback } from './pages/Auth';
import { BillingPage } from './pages/Billing';
import { DocsPage } from './pages/Docs';
import { GetStarted } from './pages/GetStarted';
import { KeysPage } from './pages/Keys';
import { MembersPage } from './pages/Members';
import { NotFound } from './pages/NotFound';
import { ReplayPage } from './pages/Replay';
import { SandboxDetail, SandboxesPage } from './pages/Sandboxes';
import { SettingsPage } from './pages/Settings';
import { CiPage, TaskDetail } from './pages/Tasks';
import { UsagePage } from './pages/Usage';
import { VerifyPage } from './pages/Verify';

function Boot() {
  return (
    <div className="boot" role="status" aria-label="Loading the console">
      <Mark />
    </div>
  );
}

/** One pageview per route, by path alone. */
function Pageviews() {
  const { pathname } = useLocation();
  useEffect(() => pageview(pathname), [pathname]);
  return null;
}

/** Tasks became CI: old links (and their filters) land on the same page there. */
function TasksMoved() {
  const { id } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/ci${id ? `/${encodeURIComponent(id)}` : ''}${search}`} replace />;
}

/** Signed-in pages: a 401 from /v1/me means "go sign in". */
function Protected({ children }: { children: ReactNode }) {
  const { me, checking } = useSession();
  const location = useLocation();
  if (checking) return <Boot />;
  if (!me) return <Navigate to={`/sign-in?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return <BillingProvider>{children}</BillingProvider>;
}

/** Signed-out pages bounce a signed-in visitor to the console, or on to where a `next` was headed (a shared replay link). */
function Public({ children }: { children: ReactNode }) {
  const { me, checking } = useSession();
  const { search } = useLocation();
  if (checking) return <Boot />;
  if (me) return <Navigate to={safeNext(new URLSearchParams(search).get('next'))} replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <SessionProvider>
      <Pageviews />
      <Routes>
        <Route path="/sign-in" element={<Public><SignIn /></Public>} />
        <Route path="/sign-up" element={<Public><SignUp /></Public>} />
        <Route path="/forgot-password" element={<Public><ForgotPassword /></Public>} />
        <Route path="/sso-callback" element={<SsoCallback />} />
        {/* The emailed links: they work signed in or out, so they are neither Public nor Protected. */}
        <Route path="/verify" element={<VerifyPage />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route element={<Protected><Shell /></Protected>}>
          {/* Home is the sandbox list; an empty one shows the first-run card, which links to the guide. */}
          <Route index element={<Navigate to="/sandboxes" replace />} />
          <Route path="get-started" element={<GetStarted />} />
          <Route path="ci" element={<CiPage />} />
          <Route path="ci/:id" element={<TaskDetail />} />
          <Route path="tasks" element={<TasksMoved />} />
          <Route path="tasks/:id" element={<TasksMoved />} />
          <Route path="sandboxes" element={<SandboxesPage />} />
          <Route path="sandboxes/:id" element={<SandboxDetail />} />
          {/* A sandbox's or task's recording; signed out, the sign-in's `next` brings a shared link back here. */}
          <Route path="r/:id" element={<ReplayPage />} />
          <Route path="keys" element={<KeysPage />} />
          <Route path="members" element={<MembersPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path={BILLING_PATH.slice(1)} element={<BillingPage />} />
          <Route path="usage" element={<UsagePage />} />
          {/* The spending limit lives on Billing now. */}
          <Route path="budget" element={<Navigate to={`${BILLING_PATH}#limit`} replace />} />
          <Route path="docs" element={<DocsPage />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </SessionProvider>
  );
}
